import {
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Bookmark,
} from "lucide-react";
import type { UseExamSessionReturn } from "../../../lib/exam/useExamSession";

/**
 * DUO chrome bottom action bar — sticky 64px blue bar matching real DUO.
 *
 *   [VORIGE]            [overzicht] [bookmark]            [VOLGENDE]
 *
 * Counter pill floats 24px above the bar, centred horizontally and
 * sliding to track the current question.
 */
export type DuoBottomBarProps = {
  session: UseExamSessionReturn;
  onOpenOverzicht: () => void;
  isBookmarked?: boolean;
  onToggleBookmark?: () => void;
};

export function DuoBottomBar({
  session,
  onOpenOverzicht,
  isBookmarked = false,
  onToggleBookmark,
}: DuoBottomBarProps) {
  const total = session.questions.length;
  const current = session.displayedIndex + 1;
  const canPrev = session.prevIdx !== null;
  const canNext = session.nextIdx !== null;

  const counterLeftPct = ((current - 0.5) / total) * 100;
  return (
    <div data-skin="duo" className="fixed bottom-0 left-0 right-0 z-20">
      <footer className="relative flex h-20 items-center justify-between bg-[var(--duo-blue)] px-6 pb-3">
        <DuoNavButton
          dir="prev"
          disabled={!canPrev}
          onClick={() => {
            if (session.prevIdx !== null) session.jumpToIndex(session.prevIdx);
          }}
        />
        <div className="flex items-center gap-4">
          <span className="duo-tooltip-host duo-tooltip-top inline-block">
            <button
              type="button"
              onClick={onOpenOverzicht}
              aria-label="Open overzicht van alle vragen"
              className="inline-flex h-10 w-10 items-center justify-center rounded-sm text-[var(--duo-blue-text)] transition-colors hover:bg-[var(--duo-blue-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--duo-blue-text)] focus-visible:outline-offset-2"
            >
              <LayoutGrid size={22} strokeWidth={1.8} aria-hidden="true" />
            </button>
            <span className="duo-tooltip" role="tooltip">Overzicht</span>
          </span>
          <span className="duo-tooltip-host duo-tooltip-top inline-block">
            <button
              type="button"
              onClick={onToggleBookmark}
              aria-label={
                isBookmarked
                  ? "Bladwijzer verwijderen van deze vraag"
                  : "Bladwijzer toevoegen aan deze vraag"
              }
              aria-pressed={isBookmarked}
              disabled={!onToggleBookmark}
              className="inline-flex h-10 w-10 items-center justify-center rounded-sm text-[var(--duo-blue-text)] transition-colors hover:bg-[var(--duo-blue-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--duo-blue-text)] focus-visible:outline-offset-2 disabled:opacity-60"
            >
              <Bookmark
                size={22}
                strokeWidth={1.8}
                fill={isBookmarked ? "currentColor" : "none"}
                aria-hidden="true"
              />
            </button>
            <span className="duo-tooltip" role="tooltip">
              Gebruik bladwijzer om later snel naar deze vraag terug te keren.
            </span>
          </span>
        </div>
        <DuoNavButton
          dir="next"
          disabled={!canNext}
          onClick={() => {
            if (session.nextIdx !== null) session.jumpToIndex(session.nextIdx);
          }}
        />
      </footer>
      <div
        aria-hidden="false"
        className="relative h-[6px] w-full bg-[var(--duo-orange)]"
      >
        <span
          className="absolute -top-6 z-10 rounded-sm bg-[var(--duo-orange)] px-2.5 py-1 text-[13px] font-semibold tabular-nums text-[var(--duo-orange-text)] shadow transition-[left] duration-300 ease-out"
          style={{
            left: `${counterLeftPct}%`,
            transform: "translateX(-50%)",
          }}
        >
          {current} / {total}
        </span>
      </div>
    </div>
  );
}

function DuoNavButton({
  dir,
  disabled,
  onClick,
}: {
  dir: "prev" | "next";
  disabled: boolean;
  onClick: () => void;
}) {
  const label = dir === "prev" ? "Vorige" : "Volgende";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={dir === "prev" ? "Naar vorige vraag" : "Naar volgende vraag"}
      className="inline-flex h-10 items-center gap-2 rounded-sm bg-[var(--duo-orange)] px-6 text-[13px] font-bold uppercase tracking-[0.04em] text-[var(--duo-orange-text)] transition-colors hover:bg-[var(--duo-orange-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--duo-blue-text)] focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {dir === "prev" ? (
        <>
          <ChevronLeft size={16} strokeWidth={2.2} aria-hidden="true" />
          <span>{label}</span>
        </>
      ) : (
        <>
          <span>{label}</span>
          <ChevronRight size={16} strokeWidth={2.2} aria-hidden="true" />
        </>
      )}
    </button>
  );
}
